import { ref, computed } from 'vue';
import type {
  Conversation,
  Message,
  AcsToken,
  ChatUser,
  CreateThreadRequest,
  SendMessageDto,
  ConversationParticipant,
  ChatFilters,
  Student,
} from '../types';
import { useChatStore } from '../stores/chatStore';
import { ChatService } from '../services';

import { logger } from '@/common/services/config';
/**
 * Composable pour gérer les actions du chat
 */
export function useChatActions() {
  const chatStore = useChatStore();

  // Local reactive state for this composable
  const isLoadingConversations = ref(false);
  const conversationsError = ref<string | null>(null);

  const userAcsToken = computed(() => chatStore.userAcsToken);
  const currentUser = computed(() => chatStore.currentUser);
  const conversations = computed(() => chatStore.conversations);
  const selectedConversation = computed(() => chatStore.selectedConversation);

  const messages = computed(() => chatStore.messages);

  // const sendMessage = async (messageData: {
  //   text: string;
  //   attachments: File[];
  //   replyTo?: string;
  // }) => {
  //   if (!selectedConversation.value) {
  //     return logger.log('Aucune conversation sélectionnée');
  //   }
  //   setSendingMessage(true);

  //   try {
  //     // await chat.sendMessage(selectedConversation.value.id, messageData.text);
  //     await chatStore.sendMessage(selectedConversation.value.id, messageData.text);
  //   } catch (err) {
  //     logger.error("Erreur lors de l'envoi du message:", err);
  //     showToast("Erreur lors de l'envoi du message");
  //   } finally {
  //     setSendingMessage(false);
  //   }
  // };

  // const sendAttachment = async (attachmentData: any) => {
  //   logger.log('Sending attachment:', attachmentData);
  //   showToast('Envoi de fichiers pas encore implémenté');
  // };

  const loadMoreMessages = async () => {
    if (!selectedConversation.value) return;

    try {
      // await chat.loadMessages(selectedConversation.value.id, 50);
      await chatStore.loadMessages(selectedConversation.value.id, 50);
    } catch (err) {
      logger.error('Erreur lors du chargement de plus de messages:', err);
    }
  };

  const loadConversations = async () => {
    const token = userAcsToken.value;

    if (!token) {
      const error = 'No user token available';
      conversationsError.value = error;
      throw new Error(error);
    }

    try {
      isLoadingConversations.value = true;
      conversationsError.value = null;

      const response = await ChatService.getConversations();
      chatStore.setConversations(response);

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversations';

      logger.error('[Chat Actions] Error details:', {
        message: errorMessage,
        stack: err instanceof Error ? err.stack : undefined,
        userAcsTokenIdentity: token.identity,
        hasUserAcsToken: !!token.token,
      });

      conversationsError.value = errorMessage;
      chatStore.setError(errorMessage);
      throw err;
    } finally {
      isLoadingConversations.value = false;
    }
  };

  const createNewConversation = async (topic: string, participants: ChatUser[]) => {
    const token = userAcsToken.value;
    const user = currentUser.value;

    if (!token || !user) {
      throw new Error('No user token or current user available');
    }

    try {
      const request: CreateThreadRequest = {
        topic,
        participantsMails: [user, ...participants].map((p) => p.email),
      };

      const conversationId = await ChatService.createConversation(request);

      const allParticipants: ConversationParticipant[] = [user, ...participants].map((p) => ({
        firstName: p.firstName,
        lastName: p.lastName,
        mail: p.email,
        photo: p.photo,
      }));

      const newConversation: Conversation = {
        id: conversationId,
        type: participants.length > 1 ? 'Group' : 'Single',
        title: topic,
        participants: allParticipants,
        unreadMessagesCount: 0,
      };

      chatStore.setConversations([newConversation, ...chatStore.conversations]);
      chatStore.setSelectedConversation(newConversation);

      return newConversation;
    } catch (err) {
      chatStore.setError(err instanceof Error ? err.message : 'Failed to create conversation');
      throw err;
    }
  };

  const deleteConversation = async (conversationId: string) => {
    const token = userAcsToken.value;

    if (!token) {
      throw new Error('No user token available');
    }

    try {
      await ChatService.deleteConversation(conversationId);

      const updatedConversations = chatStore.conversations.filter(
        (conv) => conv.id !== conversationId
      );
      chatStore.setConversations(updatedConversations);
    } catch (err) {
      chatStore.setError(err instanceof Error ? err.message : 'Failed to delete conversation');
      throw err;
    }
  };

  const showConversationDetails = () => {
    // showToast('Détails de la conversation pas encore implémentés');
  };

  const loadMessages = async (conversationId: string, limit: number) => {
    const token = userAcsToken.value;

    if (!token) {
      throw new Error('No user token available');
    }

    try {
      const rawMessages = await ChatService.getMessagesByThread(conversationId, limit);

      // Résoudre l'auteur de chaque message : 'me' ou { name, image }
      const conversation = chatStore.selectedConversation;
      const participants = conversation?.participants || [];

      const messages = rawMessages.map((msg) => {
        if (!msg.authorIdentityUser) return msg;

        const isMe = msg.authorIdentityUser === token.identity;
        if (isMe) {
          return { ...msg, author: 'me' as const };
        }

        const participant = participants.find((p) => p.identityUser === msg.authorIdentityUser);
        return {
          ...msg,
          author: {
            name: participant ? `${participant.firstName} ${participant.lastName}` : 'Utilisateur',
            image: participant?.photo || undefined,
          },
        };
      });

      chatStore.setMessages(messages);
      return messages;
    } catch (err) {
      chatStore.setError(err instanceof Error ? err.message : 'Failed to load messages');
      throw err;
    }
  };

  const markMessagesAsRead = async (conversationId: string) => {
    if (chatStore.selectedConversation?.unreadMessagesCount === 0) {
      logger.debug(
        '[Chat Actions] No unread messages to mark as read for conversation:',
        conversationId
      );
      return;
    }
    const token = userAcsToken.value;

    if (!token) {
      throw new Error('No user token available');
    }

    const unreadMessages = messages.value.filter((msg) => msg.author !== 'me' && msg.content);

    const lastUnreadMessage = unreadMessages[unreadMessages.length - 1];

    if (!lastUnreadMessage) {
      logger.debug(
        '[Chat Actions] No unread messages to mark as read for conversation:',
        conversationId
      );
      return;
    }

    try {
      await ChatService.markMessagesAsRead(conversationId, messages.value);
      const updatedConversations = chatStore.conversations.map((conv) => {
        if (conv.id === conversationId) {
          return {
            ...conv,
            unreadMessagesCount: 0,
            last_read_message_id: lastUnreadMessage.id,
          };
        }
        return conv;
      });
      chatStore.setMessages(
        messages.value.map((msg) => ({
          ...msg,
          is_read: true,
        }))
      );
      chatStore.setConversations(updatedConversations);
    } catch (err) {
      chatStore.setError(err instanceof Error ? err.message : 'Failed to mark messages as read');
      throw err;
    }
  };

  const sendMessage = async (data: {
    text: string;
    attachments: File[];
    replyTo?: string | undefined;
  }) => {
    const token = userAcsToken.value;

    if (!token) {
      throw new Error('No user token available');
    }

    if (!selectedConversation.value?.id) {
      throw new Error('No conversation id');
    }

    if (!data) {
      throw new Error('No message data provided');
    }

    const formattedData: SendMessageDto = {
      content: data.text,
      type: 'text',
      senderIdentityUser: token.identity,
    };

    try {
      await ChatService.sendMessage(selectedConversation.value?.id, formattedData);
      return formattedData;
    } catch (err) {
      chatStore.setError(err instanceof Error ? err.message : 'Failed to send message');
      throw err;
    }
  };

  const sendAttachment = async (conversationId: string, file: File) => {
    const token = userAcsToken.value;

    if (!token) {
      throw new Error('No user token available');
    }

    if (!conversationId) {
      throw new Error('No conversation id provided');
    }

    try {
      const attachmentData = {
        fileName: file.name,
        content: '', // This will be handled by FormData
      };

      const result = await ChatService.sendAttachment(conversationId, attachmentData, file);

      return result;
    } catch (err) {
      logger.error('[Chat Actions] Error sending attachment:', err);
      chatStore.setError(err instanceof Error ? err.message : 'Failed to send attachment');
      throw err;
    }
  };

  return {
    // Reactive state
    conversations,
    isLoadingConversations,
    conversationsError,
    userAcsToken,
    currentUser,

    // Actions
    loadConversations,
    createNewConversation,
    deleteConversation,
    showConversationDetails,

    loadMessages,
    markMessagesAsRead,
    sendMessage,
    sendAttachment,
    loadMoreMessages,
  };
}
